import React, { useEffect, useState } from "react";
import { Pill as PillIcon, Plus, Sparkles } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";

export default function Medications() {
  const [clients, setClients] = useState([]);
  const [meds, setMeds] = useState([]);
  const [modal, setModal] = useState(false);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ client_id: "", drug: "", dosage: "", prescriber: "", start_date: new Date().toISOString().slice(0,10), refill_schedule: "", side_effects: "" });

  const load = () => Promise.all([
    api.get("/clients").then(({ data }) => setClients(data)),
    api.get("/medications").then(({ data }) => setMeds(data)),
  ]);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/medications", form);
      toast.success("Medication added");
      setModal(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const genSummary = async (medId) => {
    try {
      const { data } = await api.post(`/medications/${medId}/summary`);
      setSummary(data.summary);
      toast.success("Summary generated");
    } catch { toast.error("Summary failed"); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page
      title="Medications"
      subtitle="Prescriptions, refills, side effects, and AI-generated care summaries."
      actions={<PrimaryButton onClick={() => setModal(true)} testid="new-med-btn"><span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Add Medication</span></PrimaryButton>}
    >
      {meds.length === 0 ? <EmptyState icon={PillIcon} title="No medications yet" description="Track prescriptions, refills, and side effects." /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {meds.map((m, i) => {
            const client = clients.find(c => c.id === m.client_id);
            return (
              <Card key={m.id} delay={Math.min(i*0.05, 0.3)} testid={`med-${m.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-serif text-xl">{m.drug}</div>
                    <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>{m.dosage}</div>
                  </div>
                  <Pill variant="success">{m.status}</Pill>
                </div>
                <div className="text-sm mb-1">{client ? `${client.first_name} ${client.last_name}` : "—"}</div>
                <div className="text-xs mb-3" style={{ color: "var(--mf-text-muted)" }}>Prescriber: {m.prescriber} · Started {m.start_date}</div>
                {m.side_effects && <div className="text-xs p-2 rounded-lg mb-2" style={{ background: "rgba(201,168,76,0.15)" }}>{m.side_effects}</div>}
                <button onClick={() => genSummary(m.id)} className="text-xs px-3 py-1.5 rounded-full flex items-center gap-1" style={{ background: "var(--mf-muted)", color: "var(--mf-primary)" }} data-testid={`med-summary-${m.id}`}>
                  <Sparkles className="w-3 h-3" /> AI Care Summary
                </button>
              </Card>
            );
          })}
        </div>
      )}

      {summary && (
        <Card className="mt-5" testid="med-summary-display">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-xl">AI-Generated Summary</h3>
            <button onClick={() => setSummary(null)} className="text-sm" style={{ color: "var(--mf-text-muted)" }}>Close</button>
          </div>
          <div className="text-sm whitespace-pre-wrap mf-prose">{summary}</div>
        </Card>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setModal(false)}>
          <div className="mf-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-4">Add Medication</h2>
            <form onSubmit={create} className="space-y-3">
              <select required value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-med-client">
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <input required placeholder="Drug name" value={form.drug} onChange={set("drug")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-med-drug" />
              <input required placeholder="Dosage (e.g. 20mg daily)" value={form.dosage} onChange={set("dosage")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-med-dose" />
              <input required placeholder="Prescriber" value={form.prescriber} onChange={set("prescriber")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-med-prescriber" />
              <DatePicker value={form.start_date} onChange={set("start_date")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-med-start" />
              <input placeholder="Refill schedule (optional)" value={form.refill_schedule} onChange={set("refill_schedule")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-med-refill" />
              <textarea placeholder="Side effects (optional)" value={form.side_effects} onChange={set("side_effects")} rows={2} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="new-med-sides"></textarea>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-sm rounded-full">Cancel</button>
                <PrimaryButton type="submit" testid="submit-new-med">Add Medication</PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </Page>
  );
}
